import { createFetchStore, createLiveStore } from './live';
import type {
  ClientKey,
  Deployment,
  Estate,
  PoolKey,
  RouterTokenRecord,
  TenantProviderKey,
  TenantSummary,
  UsageRow,
  Memory,
  MemoryListQuery,
  MemoryTenant,
  MemoryTenantsQuery,
} from '../data/types';
import { tenantKey } from '../data/types';
import type { LedgerQuery } from './sources/types';

/* One store per watched collection. Each owns its own watch, so a screen that
 * needs three of them opens three independent generations and a slow one cannot
 * hold up the others. */

/* No comparator: a watch's order is its query's order, and re-sorting client
 * side would fight whatever `orderBy` the rule-approved query ends up using. */
export const useEstatesStore = createLiveStore<Estate, void>((src, _q, on) =>
  src.watchEstates(on),
);

export const useDeploymentsStore = createLiveStore<Deployment, string | undefined>(
  (src, estateId, on) => src.watchDeployments(estateId, on),
  (a, b) => a.doc.id.localeCompare(b.doc.id),
);

export const useTokensStore = createLiveStore<RouterTokenRecord, string | undefined>(
  (src, estateId, on) => src.watchTokens(estateId, on),
  (a, b) => b.doc.issuedAt.localeCompare(a.doc.issuedAt),
);

export const useClientKeysStore = createLiveStore<ClientKey, string | undefined>(
  (src, estateId, on) => src.watchClientKeys(estateId, on),
  (a, b) => a.doc.label.localeCompare(b.doc.label),
);

/** Newest first — the ledger is read from the top, and a live row must appear
 *  there rather than at the bottom of the page. */
export const useLedgerStore = createLiveStore<UsageRow, LedgerQuery>(
  (src, q, on) => src.watchLedger(q, on),
  (a, b) => b.doc.occurredAt.localeCompare(a.doc.occurredAt),
);

/* The pool is a promise, not a watch: its collection carries raw token material
 * and cannot be exposed to a browser at all (spec/console.md §Two data lanes).
 * It still lives in a Collection so the table primitive and the no-flash rules
 * are the same ones — only the transport differs. */

export const usePoolStore = createFetchStore<PoolKey, void>(
  (src) => src.listPoolKeys(),
  (k) => k.id,
);

export const useTenantKeysStore = createFetchStore<
  TenantProviderKey,
  { estateId: string; tenantId: string }
>((src, q) => src.listTenantKeys(q.estateId, q.tenantId), (k) => k.id);

/* Tenants are a real collection now: the registry that names each tenant's
 * BigQuery shard (DR-37). It replaced the view-model placeholder this store
 * used to be, so the screens that list tenants read live rows like every other
 * table — and the day the derived counts land, only this file changes. */
export const useTenantsStore = createLiveStore<TenantSummary, string | undefined>(
  (src, estateId, on) => src.watchTenants(estateId, on),
  (a, b) => a.doc.displayName.localeCompare(b.doc.displayName),
);

/* Memories come through the admin API, one page per load, with the source's
 * cursor riding the reset into the collection (DR-75). Rows key on the
 * document id, so a forget refreshes one row via upsert and nothing flashes. */
export const useMemoriesStore = createFetchStore<Memory, MemoryListQuery>(
  (src, q) => src.listMemories(q),
  (m) => m.id,
);

/* The estate overview (DR-86): one page of an estate's tenants and their
 * counts. Rows key on the full (estate, tenant) tuple — two estates may use
 * the same tenant id. */
export const useMemoryTenantsStore = createFetchStore<MemoryTenant, MemoryTenantsQuery>(
  (src, q) => src.memoryTenants(q),
  (t) => tenantKey(t.estateId, t.tenantId),
);
